import { Fragment, type CSSProperties, type ReactNode } from 'react'
import TextEditorLink from '../TextEditor/components/TextEditorLink/TextEditorLink'
import TextEditorMention from '../TextEditor/components/TextEditorMention/TextEditorMention'
import TextEditorCodeLeaf from '../TextEditor/components/TextEditorCodeLeaf/TextEditorCodeLeaf'
import StaticImage from './components/StaticImage'
import StaticFile from './components/StaticFile'
import StaticFigma from './components/StaticFigma'
import StaticHr from './components/StaticHr'
import css from './TextStatic.module.css'

// ─────────────────────────────────────────────────────────────────────────────
// Read-only renderer for saved Plate content.
//
// This is a small, dependency-free serializer — it walks the Plate node array and
// emits React, WITHOUT pulling the heavy Plate/Slate library. That's the whole point:
// displaying a task description or comment must not load the ~570 KB editor chunk, so
// the text paints together with the task modal instead of a frame later (SCD-867).
//
// PARITY: this is the second renderer of the same content (the editable one is
// `TextEditor`). It must produce the same DOM/inline styles Plate produces for each
// node type so read view and editor look identical. When you add/change a node type,
// mark, or element component in `TextEditor`, mirror it here in the same change.
// See CLAUDE.md → "Plate.js (rich text editor)".
// ─────────────────────────────────────────────────────────────────────────────

const INDENT_SIZE = 24 // px per indent level — matches Plate's indent rendering

const ORDERED_LIST_TYPES = new Set([
    'decimal', 'decimal-leading-zero',
    'lower-alpha', 'upper-alpha', 'lower-latin', 'upper-latin',
    'lower-roman', 'upper-roman',
])

// H1 is rendered as H2 to mirror the editor, which no longer allows H1 and downgrades any
// h1 (paste/legacy) to h2 (SCD-869 / NoH1Plugin). Keeps read view and edit view in sync.
const HEADING_TAGS: Record<string, 'h2' | 'h3'> = { h1: 'h2', h2: 'h2', h3: 'h3' }

const VOID_COMPONENTS: Record<string, (props: any) => ReactNode> = {
    img: StaticImage,
    file: StaticFile,
    figma: StaticFigma,
    hr: StaticHr,
}

// Plate's editable renders with white-space: pre-wrap, so a newline inside a text leaf shows as a
// line break. The static view has no pre-wrap (newlines would collapse to a space), so reproduce
// the break explicitly with <br/> — otherwise content split across lines by a soft break (e.g. a
// bold label on its own line) silently joins onto one line here. Keep in sync with TextEditor.
function renderText(text: string): ReactNode {
    if (!text.includes('\n')) return text
    return text.split('\n').map((part, i) => (
        <Fragment key={i}>{i > 0 ? <br /> : null}{part}</Fragment>
    ))
}

function renderLeaf(node: any, key: number): ReactNode {
    let content: ReactNode = renderText(node.text)
    if (node.bold) content = <strong>{content}</strong>
    if (node.italic) content = <em>{content}</em>
    if (node.underline) content = <u>{content}</u>
    if (node.strikethrough) content = <s>{content}</s>
    if (node.code) content = <TextEditorCodeLeaf attributes={{}}>{content}</TextEditorCodeLeaf>
    return <Fragment key={key}>{content}</Fragment>
}

function renderInline(node: any, key: number): ReactNode {
    if (typeof node.text === 'string') return renderLeaf(node, key)
    const children = renderChildren(node.children)
    if (node.type === 'a') {
        return <TextEditorLink key={key} attributes={{}} element={node}>{children}</TextEditorLink>
    }
    if (node.type === 'mention') {
        return <TextEditorMention key={key} attributes={{}} element={node}>{children}</TextEditorMention>
    }
    return <Fragment key={key}>{children}</Fragment>
}

function renderChildren(children: any[] | undefined): ReactNode {
    if (!Array.isArray(children)) return null
    return children.map(renderInline)
}

function isEmptyBlock(node: any): boolean {
    if (!Array.isArray(node.children)) return true
    return node.children.every((c: any) => typeof c.text === 'string' && c.text === '')
}

function collectText(node: any): string {
    if (typeof node?.text === 'string') return node.text
    if (Array.isArray(node?.children)) return node.children.map(collectText).join('')
    return ''
}

// Visible content = any text, or any void block (image / file / figma / hr). A value of just
// empty paragraph(s) counts as empty (so the placeholder shows, matching the editor).
function hasVisibleContent(nodes: any[]): boolean {
    return nodes.some((node: any) =>
        (node?.type && node.type in VOID_COMPONENTS) || collectText(node).trim().length > 0
    )
}

// Exact DOM Slate renders for its placeholder (slate-react DefaultPlaceholder), so the read
// view's empty state lines up pixel-for-pixel with the editor's. Opacity/color come from the
// global `[data-slate-placeholder='true']` rule (same as the editor) — not set inline here.
const PLACEHOLDER_STYLE: CSSProperties = {
    position: 'absolute',
    top: 0,
    pointerEvents: 'none',
    width: '100%',
    maxWidth: '100%',
    display: 'block',
    userSelect: 'none',
    textDecoration: 'none',
}

function renderBlock(node: any, key: number, start: number | undefined): ReactNode {
    const Void = node?.type && VOID_COMPONENTS[node.type]
    if (Void) return <Void key={key} attributes={{}} element={node} />

    const indent = typeof node.indent === 'number' ? node.indent : 0
    const marginLeft = indent > 0 ? indent * INDENT_SIZE : undefined
    const inner: ReactNode = isEmptyBlock(node) ? <br /> : renderChildren(node.children)

    // Indent-based list item (same shape Plate renders: a margin wrapper + a single-item
    // <ul>/<ol> whose `start` fakes numbering continuation across sibling items). The
    // `data-slate-node="element"` matches the index.css `:has(> ul/ol)` spacing rule.
    if (node.listStyleType) {
        const Tag = ORDERED_LIST_TYPES.has(node.listStyleType) ? 'ol' : 'ul'
        return (
            <div key={key} data-slate-node="element" style={marginLeft ? { marginLeft } : undefined}>
                <Tag style={{ listStyleType: node.listStyleType, margin: 0, padding: 0 }} start={start}>
                    <li>{inner}</li>
                </Tag>
            </div>
        )
    }

    // Plate renders paragraphs (and unknown blocks) as <div>, not <p> — match it so the
    // index.css element rules (and non-rules, like the inert `p + p`) line up exactly.
    const Tag = HEADING_TAGS[node.type] ?? (node.type === 'blockquote' ? 'blockquote' : 'div')
    return <Tag key={key} style={marginLeft ? { marginLeft } : undefined}>{inner}</Tag>
}

// Per-node `start` for ordered lists: contiguous siblings of the same indent + list type
// continue numbering (2, 3, …); anything else resets the run.
function computeListStarts(nodes: any[]): (number | undefined)[] {
    let lastKey: string | null = null
    let count = 0
    return nodes.map(node => {
        const key = node.listStyleType ? `${node.indent ?? 0}:${node.listStyleType}` : null
        if (key && ORDERED_LIST_TYPES.has(node.listStyleType)) {
            count = key === lastKey ? count + 1 : 1
            lastKey = key
            return count > 1 ? count : undefined
        }
        lastKey = key
        count = 0
        return undefined
    })
}

function toNodes(value: unknown): any[] | null {
    if (Array.isArray(value) && value.length > 0) return value
    if (typeof value === 'string' && value.trim()) {
        return [{ type: 'p', children: [{ text: value }] }]
    }
    return null
}

export interface TextStaticProps {
    value: unknown
    // When the value is empty, render this as the editor's placeholder (same DOM as Slate's),
    // so an empty description swaps to the editor with no flicker. Omit it (comments) to render
    // nothing when empty.
    placeholder?: string
}

const TextStatic = ({ value, placeholder }: TextStaticProps) => {
    const nodes = toNodes(value)

    // `data-slate-editor="true"` makes the shared editor typography in index.css
    // (`[data-slate-editor='true'] h1/h2/h3/p+p/blockquote/code/…`) apply here too, so the
    // read view matches the editor exactly without duplicating any of those rules.
    if (!nodes || !hasVisibleContent(nodes)) {
        if (!placeholder) return null
        return (
            <div className={css.content} data-slate-editor="true">
                <div style={{ position: 'relative' }}>
                    <span data-slate-placeholder="true" style={PLACEHOLDER_STYLE}>{placeholder}</span>
                    <br />
                </div>
            </div>
        )
    }

    const starts = computeListStarts(nodes)
    return (
        <div className={css.content} data-slate-editor="true">
            {nodes.map((node, i) => renderBlock(node, i, starts[i]))}
        </div>
    )
}

export default TextStatic
